import { ChecklistColumnConfig } from "@/types/stockChecklist";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";

interface ColumnSelectionSectionProps {
  columns: ChecklistColumnConfig;
  onChange: (columns: ChecklistColumnConfig) => void;
  /** Full list of company price tables — enables per-table column selection */
  availablePriceTables?: Array<{ id: string; name: string }>;
}

interface ColumnDef {
  key: keyof ChecklistColumnConfig;
  label: string;
  /** How many physical table columns this option adds */
  colWeight: number;
  hint?: string;
}

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'unit',       label: 'Unidade de Medida',  colWeight: 1 },
  { key: 'category',   label: 'Categoria',           colWeight: 1 },
  { key: 'ean',        label: 'Código de Barras',    colWeight: 1 },
  {
    key: 'sale_price',
    label: 'Preço de Venda',
    colWeight: 2,
    hint: 'Exibe "Atual" + "Novo" (2 colunas)',
  },
  {
    key: 'cost_price',
    label: 'Preço de Custo',
    colWeight: 2,
    hint: 'Exibe "Atual" + "Novo" (2 colunas)',
  },
  { key: 'photo', label: 'Foto do Produto', colWeight: 1 },
];

/** Fixed columns always present: Status, SKU, Nome, Estoque, Contagem, Dif. */
const FIXED_COLUMNS = 6;

function getColumnCount(columns: ChecklistColumnConfig): number {
  const staticCols = COLUMN_DEFS.reduce(
    (acc, def) => acc + (columns[def.key] ? def.colWeight : 0),
    FIXED_COLUMNS
  );
  // Each selected price table adds 2 columns
  const priceTableCols = (columns.price_tables?.length ?? 0) * 2;
  return staticCols + priceTableCols;
}

function getComplexityBadge(count: number) {
  if (count <= 10)
    return { label: 'Boa visualização', className: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30' };
  if (count <= 13)
    return { label: 'Pode ficar apertado em tablet', className: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30' };
  return { label: 'Não recomendado para tablet', className: 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30' };
}

export const ColumnSelectionSection = ({
  columns,
  onChange,
  availablePriceTables = [],
}: ColumnSelectionSectionProps) => {
  const handleToggle = (key: keyof ChecklistColumnConfig) => {
    onChange({ ...columns, [key]: !columns[key] });
  };

  const handlePriceTableToggle = (table: { id: string; name: string }) => {
    const current = columns.price_tables ?? [];
    const exists = current.some((t) => t.id === table.id);
    onChange({
      ...columns,
      price_tables: exists
        ? current.filter((t) => t.id !== table.id)
        : [...current, table],
    });
  };

  const totalCols = getColumnCount(columns);
  const badge = getComplexityBadge(totalCols);

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-foreground">Colunas Opcionais</p>
      <p className="text-xs text-muted-foreground">
        Colunas fixas (Status, SKU, Nome, Estoque, Contagem, Dif.) são sempre exibidas.
      </p>

      <div className="grid grid-cols-2 gap-2 p-3 border rounded-md bg-muted/30">
        {COLUMN_DEFS.map(({ key, label, hint }) => (
          <label key={key} className="flex items-start gap-2 cursor-pointer py-1.5">
            <Checkbox
              checked={columns[key]}
              onCheckedChange={() => handleToggle(key)}
              className="mt-0.5"
            />
            <span className="text-sm leading-snug">
              {label}
              {hint && (
                <span className="block text-xs text-muted-foreground">{hint}</span>
              )}
            </span>
          </label>
        ))}
      </div>

      {/* Price tables — optional per-table dual columns */}
      {availablePriceTables.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-foreground/80">
            Tabelas de Preço <span className="text-muted-foreground font-normal">(2 colunas por tabela: Atual + Novo)</span>
          </p>
          <div className="grid grid-cols-2 gap-2 p-3 border rounded-md bg-muted/30">
            {availablePriceTables.map((table) => (
              <label key={table.id} className="flex items-center gap-2 cursor-pointer py-1">
                <Checkbox
                  checked={(columns.price_tables ?? []).some((t) => t.id === table.id)}
                  onCheckedChange={() => handlePriceTableToggle(table)}
                />
                <span className="text-sm truncate">{table.name}</span>
              </label>
            ))}
          </div>

          {/* F2: warn when ≥4 price tables are available */}
          {availablePriceTables.length >= 4 && (
            <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p className="text-xs leading-snug">
                Atenção: muitas tabelas de preço selecionadas podem comprimir a visualização em tablets.
              </p>
            </div>
          )}
        </div>
      )}

      {/* B4: Column complexity indicator */}
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={badge.className}>
          {totalCols} colunas — {badge.label}
        </Badge>
      </div>
    </div>
  );
};
